'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EcosystemRestorationProps {
  currentLanguage: string;
}

const EcosystemRestoration = ({ currentLanguage }: EcosystemRestorationProps) => {
  const [selectedProject, setSelectedProject] = useState(0);

  const content = {
    en: {
      title: 'Ecosystem Restoration Projects',
      subtitle: 'Desert and post-flood rejuvenation with advanced monitoring',
      projects: [
        {
          type: 'Desert Rejuvenation',
          icon: 'GlobeAltIcon',
          description: 'Transform arid landscapes into thriving ecosystems',
          metrics: [
            { label: 'Area Covered', value: '50K+ hectares' },
            { label: 'Biodiversity Increase', value: '300%' },
            { label: 'Carbon Sequestered', value: '500K tons' }
          ],
          activities: [
            'Native vegetation planting',
            'Soil health restoration',
            'Water harvesting systems',
            'Wildlife corridor establishment'
          ]
        },
        {
          type: 'Post-Flood Recovery',
          icon: 'CloudIcon',
          description: 'Comprehensive flood damage assessment and ecosystem recovery',
          metrics: [
            { label: 'Communities Served', value: '100+' },
            { label: 'Recovery Rate', value: '85%' },
            { label: 'Water Quality Improved', value: '90%' }
          ],
          activities: [
            'Rapid damage assessment',
            'Contamination cleanup',
            'Infrastructure rehabilitation',
            'Community resilience building'
          ]
        }
      ],
      trackingFeatures: [
        { name: 'IoT Sensor Networks', icon: 'SignalIcon' },
        { name: 'Satellite Monitoring', icon: 'GlobeAltIcon' },
        { name: 'AI-Powered Analytics', icon: 'CpuChipIcon' },
        { name: 'Real-time Reporting', icon: 'ChartBarIcon' }
      ]
    },
    hi: {
      title: 'पारिस्थितिकी तंत्र बहाली परियोजनाएं',
      subtitle: 'उन्नत निगरानी के साथ रेगिस्तान और बाढ़ के बाद कायाकल्प',
      projects: [
        {
          type: 'रेगिस्तान कायाकल्प',
          icon: 'GlobeAltIcon',
          description: 'शुष्क परिदृश्य को संपन्न पारिस्थितिक तंत्र में बदलें',
          metrics: [
            { label: 'कवर क्षेत्र', value: '50K+ हेक्टेयर' },
            { label: 'जैव विविधता वृद्धि', value: '300%' },
            { label: 'कार्बन अलग किया गया', value: '500K टन' }
          ],
          activities: [
            'देशी वनस्पति रोपण',
            'मिट्टी स्वास्थ्य बहाली',
            'जल संचयन प्रणाली',
            'वन्यजीव गलियारा स्थापना'
          ]
        },
        {
          type: 'बाढ़ के बाद रिकवरी',
          icon: 'CloudIcon',
          description: 'व्यापक बाढ़ क्षति मूल्यांकन और पारिस्थितिकी तंत्र रिकवरी',
          metrics: [
            { label: 'समुदाय सेवित', value: '100+' },
            { label: 'रिकवरी दर', value: '85%' },
            { label: 'जल गुणवत्ता में सुधार', value: '90%' }
          ],
          activities: [
            'त्वरित क्षति मूल्यांकन',
            'संदूषण सफाई',
            'बुनियादी ढांचा पुनर्वास',
            'समुदाय लचीलापन निर्माण'
          ]
        }
      ],
      trackingFeatures: [
        { name: 'IoT सेंसर नेटवर्क', icon: 'SignalIcon' },
        { name: 'उपग्रह निगरानी', icon: 'GlobeAltIcon' },
        { name: 'AI-संचालित विश्लेषण', icon: 'CpuChipIcon' },
        { name: 'वास्तविक समय रिपोर्टिंग', icon: 'ChartBarIcon' }
      ]
    },
    ar: {
      title: 'مشاريع استعادة النظام البيئي',
      subtitle: 'تجديد الصحراء وما بعد الفيضانات بمراقبة متقدمة',
      projects: [
        {
          type: 'تجديد الصحراء',
          icon: 'GlobeAltIcon',
          description: 'تحويل المناظر الطبيعية القاحلة إلى نظم بيئية مزدهرة',
          metrics: [
            { label: 'المساحة المغطاة', value: '50K+ هكتار' },
            { label: 'زيادة التنوع البيولوجي', value: '300%' },
            { label: 'الكربون المحتجز', value: '500K طن' }
          ],
          activities: [
            'زراعة النباتات الأصلية',
            'استعادة صحة التربة',
            'أنظمة حصاد المياه',
            'إنشاء ممر الحياة البرية'
          ]
        },
        {
          type: 'التعافي بعد الفيضانات',
          icon: 'CloudIcon',
          description: 'تقييم شامل لأضرار الفيضانات واستعادة النظام البيئي',
          metrics: [
            { label: 'المجتمعات المخدومة', value: '100+' },
            { label: 'معدل التعافي', value: '85%' },
            { label: 'تحسين جودة المياه', value: '90%' }
          ],
          activities: [
            'تقييم سريع للأضرار',
            'تنظيف التلوث',
            'إعادة تأهيل البنية التحتية',
            'بناء مرونة المجتمع'
          ]
        }
      ],
      trackingFeatures: [
        { name: 'شبكات أجهزة استشعار IoT', icon: 'SignalIcon' },
        { name: 'المراقبة عبر الأقمار الصناعية', icon: 'GlobeAltIcon' },
        { name: 'التحليلات المدعومة بالذكاء الاصطناعي', icon: 'CpuChipIcon' },
        { name: 'التقارير في الوقت الفعلي', icon: 'ChartBarIcon' }
      ]
    }
  };

  const currentContent = content[currentLanguage as keyof typeof content] || content.en;
  const currentProject = currentContent.projects[selectedProject];

  return (
    <section className="py-16 md:py-24 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary">
            {currentContent.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        {/* Project Selector */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
          {currentContent.projects.map((project, index) => (
            <button
              key={index}
              onClick={() => setSelectedProject(index)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                selectedProject === index
                  ? 'bg-primary text-primary-foreground shadow-natural'
                  : 'bg-card text-text-secondary hover:bg-card/80 border border-border'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name={project.icon as any} size={20} />
                <span>{project.type}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Project Details */}
        <div className="bg-card border border-border rounded-xl p-8 shadow-natural mb-8">
          <p className="text-lg text-text-secondary mb-8 text-center">
            {currentProject.description}
          </p>

          {/* Metrics */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {currentProject.metrics.map((metric, index) => (
              <div 
                key={index}
                className="text-center p-6 bg-background rounded-lg border border-border"
              >
                <div className="text-3xl font-bold text-primary mb-2">
                  {metric.value}
                </div>
                <div className="text-sm text-text-secondary">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>

          {/* Activities */}
          <div>
            <h3 className="font-heading font-bold text-xl text-text-primary mb-4">
              Key Activities:
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              {currentProject.activities.map((activity, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 p-4 bg-background rounded-lg border border-border"
                >
                  <Icon name="CheckCircleIcon" size={20} className="text-primary flex-shrink-0" />
                  <span className="text-text-secondary">{activity}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tracking Features */}
        <div>
          <h3 className="font-heading font-bold text-2xl text-text-primary mb-6 text-center">
            Advanced Monitoring & Tracking
          </h3>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {currentContent.trackingFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:border-primary/50 hover:shadow-natural transition-all duration-300"
              >
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={feature.icon as any} size={28} className="text-primary" />
                </div>
                <h4 className="font-semibold text-text-primary">{feature.name}</h4>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcosystemRestoration;